import { Container, Section } from '@resolute/ui';
import {
  Archive,
  Bath,
  BedDouble,
  BookOpen,
  ChefHat,
  Compass,
  DoorOpen,
  Droplet,
  Flame,
  Milestone,
  Sofa,
  Users,
  UtensilsCrossed,
  type LucideIcon,
} from 'lucide-react';

import { Reveal, StaggerGroup, StaggerItem } from '@/components/motion/reveal';
import { PageHero } from '@/components/page-hero';
import { Seo } from '@/lib/seo';

interface VaastuTip {
  icon: LucideIcon;
  room: string;
  direction: string;
  body: string;
}

const TIPS: VaastuTip[] = [
  {
    icon: DoorOpen,
    room: 'Main entrance / front door',
    direction: 'North, East or North-East',
    body: 'The main door is the primary entry point for energy into the home, so North, East and North-East facing doors are considered most auspicious. A South-West main door is best avoided.',
  },
  {
    icon: ChefHat,
    room: 'Kitchen',
    direction: 'South-East',
    body: 'The South-East corner is ruled by Agni, the fire element, making it the ideal spot for the kitchen. Cook while facing East. A kitchen in the North-East is best avoided.',
  },
  {
    icon: BedDouble,
    room: 'Master bedroom',
    direction: 'South-West',
    body: "The South-West is the most stable, grounded zone of a home — well suited to the head of the household's bedroom. Sleep with your head towards the South or East; avoid the head pointing North.",
  },
  {
    icon: Users,
    room: "Children's bedroom",
    direction: 'West or North-West',
    body: 'A West or North-West bedroom supports focus and stability, and works well for children or younger family members.',
  },
  {
    icon: BedDouble,
    room: 'Guest bedroom',
    direction: 'North-West',
    body: "The North-West governs movement and change, which suits a guest room — keeping a visitor's stay comfortable without them overstaying.",
  },
  {
    icon: Sofa,
    room: 'Living / drawing room',
    direction: 'North, East or North-East',
    body: 'The living room welcomes guests and outside energy, so it works best facing North, East or North-East, with seating arranged so guests face East or North.',
  },
  {
    icon: Flame,
    room: 'Pooja room / temple',
    direction: 'North-East',
    body: 'The North-East (Ishaan corner) is the most sacred zone of the home. Place the pooja room here, with the deity facing East or West and the person praying facing East.',
  },
  {
    icon: UtensilsCrossed,
    room: 'Dining room',
    direction: 'West or South',
    body: 'A dining area in the West or South, with the family facing East while eating, is considered to support good digestion and family harmony.',
  },
  {
    icon: Bath,
    room: 'Bathroom / toilet',
    direction: 'North-West',
    body: 'Bathrooms and toilets sit best in the North-West. Strictly avoid the North-East and South-West, the two most sensitive zones of the home.',
  },
  {
    icon: Milestone,
    room: 'Staircase',
    direction: 'South, West or South-West',
    body: "A staircase carries the house's rising energy, so it belongs in the South, West or South-West. Never place one in the North-East or at the exact centre of the home.",
  },
  {
    icon: BookOpen,
    room: 'Study room',
    direction: 'North-East or East',
    body: 'A North-East or East-facing study, with the student facing East or North while studying, is considered to support concentration and learning.',
  },
  {
    icon: Archive,
    room: 'Store room',
    direction: 'South-West or North-West',
    body: 'Heavy storage belongs in the South-West (for stability) or North-West — kept away from the light, open North-East corner.',
  },
  {
    icon: Droplet,
    room: 'Water source (borewell / underground tank)',
    direction: 'North-East',
    body: 'Underground water sources are best sunk in the North-East, the zone associated with the water element.',
  },
  {
    icon: Compass,
    room: 'Overhead water tank',
    direction: 'South-West or West',
    body: 'An elevated tank on the South-West or West of the roof balances its weight against the lighter, more open North-East.',
  },
];

const PRINCIPLES: string[] = [
  'Keep the centre of the house (the Brahmasthan) open, light and free of clutter or heavy structures — it is considered the energetic core of the home.',
  'A square or rectangular plot/floor plan is preferred; avoid a missing or cut corner in the North-East wherever possible.',
  'Keep heavier furniture, taller structures and storage in the South and West; keep the North and East lighter and more open.',
  'If extending the house, the North or East are the most favourable directions; avoid extending towards the South-West.',
  'Mirrors are best placed on North or East walls, and never positioned facing the bed.',
  'Avoid placing a toilet directly above or below the kitchen or the pooja room.',
];

export default function VaastuTipsPage() {
  return (
    <>
      <Seo
        title="Vaastu Tips for Your Home"
        description="Traditional Vastu Shastra guidance for every room — where the kitchen, main door, bedrooms, living room, pooja room and more should ideally face in your home."
        path="/vaastu-tips"
      />

      <PageHero
        eyebrow="Vaastu Shastra"
        title="Vaastu tips for your home."
        titleLine2="Traditional guidance."
        description="Traditional guidance on where each room and element of a home should ideally sit — from the main door to the pooja room — so you can plan or evaluate a property with Vastu in mind."
      />

      <Section>
        <Container className="max-w-5xl">
          <Reveal>
            <h2 className="font-display text-2xl font-semibold tracking-tight">
              Room-by-room guidance
            </h2>
          </Reveal>

          {/* 14 cards across up to 5 rows — spans multiple screens, so each
              card reveals independently on its own scroll entry (StaggerGroup
              would resolve almost instantly since it triggers off the
              container's top edge, leaving the lower rows already "revealed"
              by the time they're actually scrolled to). */}
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {TIPS.map(({ icon: Icon, room, direction, body }) => (
              <Reveal
                key={room}
                className="flex flex-col gap-3 rounded-xl border border-border bg-card p-6"
              >
                <span className="flex size-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Icon className="size-5" aria-hidden="true" />
                </span>
                <div>
                  <h3 className="text-lg font-semibold">{room}</h3>
                  <p className="mt-0.5 text-sm font-medium text-secondary">{direction}</p>
                </div>
                <p className="text-sm text-muted-foreground">{body}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      <Section spacing="md" className="border-t border-border bg-muted/30">
        <Container className="max-w-3xl">
          <Reveal>
            <h2 className="font-display text-2xl font-semibold tracking-tight">
              General principles
            </h2>
          </Reveal>
          <StaggerGroup as="ul" className="mt-6 space-y-3">
            {PRINCIPLES.map((p) => (
              <StaggerItem key={p} as="li" className="flex gap-3 text-muted-foreground">
                <span
                  aria-hidden="true"
                  className="mt-2 size-1.5 shrink-0 rounded-full bg-primary"
                />
                {p}
              </StaggerItem>
            ))}
          </StaggerGroup>

          <Reveal className="mt-8 rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground">
            <strong className="text-foreground">A note on this guidance:</strong> these are
            traditional Vastu Shastra principles, offered as general, educational information for
            homebuyers. They aren&apos;t a substitute for a qualified Vastu consultant&apos;s
            site-specific assessment, and have no bearing on a property&apos;s RERA verification or
            legal status on this site.
          </Reveal>
        </Container>
      </Section>
    </>
  );
}
